/**
 * I lavori previsti sul telefono (7/10/2026): il primo gruppo della scheda del
 * progetto, perché è la domanda che si fa chi la apre ("a che punto siamo con
 * quello che abbiamo promesso?").
 *
 * Sul computer è una scheda a sé (LavoriTab) con quattro numeri in griglia e
 * una tabella. Qui: una riga per lavoro, prima quelli fermi, in fondo i fatti;
 * l'icona dice lo stato, la riga sotto dice chi lo tiene fermo e da quando.
 * Toccando una riga sale il pannello con gli stati (le stesse voci della
 * tendina del computer). Stesse chiamate, stesse finestre per il fermo, per
 * caricare dal preventivo e per il resoconto al cliente: sono le stesse
 * funzioni di LavoriTab, importate da lì.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { MEmpty, MGroup, MMore, MRow } from '../../components/mobile/MobileKit';
import {
  type LavoroTask,
  type ComunicazioneProgetto,
  type Stato,
  type Aggiornamento,
  STATI,
  statoDi,
  giorniDa,
  daQuanto,
  apriTask,
  SuTutto,
  VOCI_STATO,
  TAG_RESOCONTO,
  FermoDialog,
  CaricaDalPreventivo,
  ResocontoAlCliente,
} from './LavoriTab';
import { RigaAzione, giornoBreve, personaBreve } from './pjmPezzi';

const PRIMI = 6;

/** Le stesse icone delle voci del menu dello stato. */
const ICONA: Record<Stato, IconName> = {
  FERMO_CLIENTE: 'user', FERMO_NOI: 'alert', FERMO: 'alert', IN_PROGRESS: 'zap', REVIEW: 'send', TODO: 'clock', DONE: 'check',
};

const fermo = (s: Stato) => s === 'FERMO_CLIENTE' || s === 'FERMO_NOI' || s === 'FERMO';

export function LavoriMobile({ projectId, tasks, communications }: {
  projectId: string;
  tasks: LavoroTask[];
  communications: ComunicazioneProgetto[];
}) {
  const qc = useQueryClient();
  const [importa, setImporta] = useState(false);
  const [resoconto, setResoconto] = useState(false);
  const [menu, setMenu] = useState<LavoroTask | null>(null);
  const [inFermo, setInFermo] = useState<{ lavoro: LavoroTask; chi: 'CLIENTE' | 'NOI' } | null>(null);
  const [nuovo, setNuovo] = useState('');
  const [aMano, setAMano] = useState(false);
  const [tutti, setTutti] = useState(false);

  // Lo stesso ordine della scheda del computer: prima chi aspetta qualcuno,
  // in fondo il fatto; a parità di stato l'ordine del preventivo.
  const lavori = useMemo(
    () => tasks
      .filter((t) => t.inScope)
      .map((t, i) => ({ t, i, s: statoDi(t) }))
      .sort((a, b) => STATI[a.s].ordine - STATI[b.s].ordine || a.i - b.i),
    [tasks],
  );
  const totale = lavori.length;
  const ultimo = communications
    .filter((c) => c.tag === TAG_RESOCONTO)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0];
  const giorniUltimo = giorniDa(ultimo?.occurredAt);

  const invalida = () => qc.invalidateQueries({ queryKey: ['project', projectId] });
  const aggiorna = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Aggiornamento }) => api.patch(`/projects/${projectId}/lavori/${id}`, body),
    onSuccess: invalida,
    onError: (e: any) => toast.alert('Lavoro non aggiornato', e?.message ?? 'Riprova'),
  });
  const aggiungi = useMutation({
    mutationFn: (title: string) => api.post(`/projects/${projectId}/lavori`, { voci: [{ title }] }),
    onSuccess: () => { setNuovo(''); invalida(); },
    onError: (e: any) => toast.alert('Lavoro non aggiunto', e?.message ?? 'Riprova'),
  });

  const cambiaStato = (t: LavoroTask, s: Stato) => {
    if (s === 'FERMO_CLIENTE' || s === 'FERMO_NOI') { setInFermo({ lavoro: t, chi: s === 'FERMO_CLIENTE' ? 'CLIENTE' : 'NOI' }); return; }
    aggiorna.mutate({ id: t.id, body: { status: s === 'FERMO' ? 'BLOCKED' : s } });
  };
  const salvaNuovo = () => { const t = nuovo.trim(); if (t && !aggiungi.isPending) aggiungi.mutate(t); };

  const visibili = tutti ? lavori : lavori.slice(0, PRIMI);
  const statoMenu = menu ? statoDi(menu) : null;

  const campo = (
    <div className="pjm-add">
      <Icon name="plus" size={14} />
      <input
        className="inp"
        enterKeyHint="done"
        placeholder="Aggiungi un lavoro previsto"
        aria-label="Nuovo lavoro previsto"
        value={nuovo}
        autoFocus={aMano && totale === 0}
        onChange={(e) => setNuovo(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); salvaNuovo(); }
          else if (e.key === 'Escape') setNuovo('');
        }}
      />
      {nuovo.trim() && (
        <button type="button" className="btn btn-sm btn-primary" disabled={aggiungi.isPending} onClick={salvaNuovo}>Aggiungi</button>
      )}
    </div>
  );

  return (
    <>
      <MGroup
        title="Lavori previsti"
        count={totale || undefined}
        action={totale > 0 ? { label: 'Resoconto al cliente', onClick: () => setResoconto(true) } : undefined}
        note={totale > 0
          ? (ultimo
            ? `Ultimo resoconto al cliente ${giorniUltimo === 0 ? 'oggi' : giorniUltimo === 1 ? 'ieri' : `${giorniUltimo} giorni fa`}${ultimo.toText ? `, a ${ultimo.toText}` : ''}.`
            : 'Il resoconto al cliente non è ancora stato mandato.')
          : undefined}
      >
        {totale === 0 ? (
          <>
            <MEmpty>Nessun lavoro previsto: ogni riga del preventivo diventa un lavoro con il suo stato.</MEmpty>
            <RigaAzione icon="file" label="Carica dal preventivo" onClick={() => setImporta(true)} />
            {aMano ? campo : <RigaAzione icon="pencil" label="Scrivi un lavoro a mano" onClick={() => setAMano(true)} />}
          </>
        ) : (
          <>
            {visibili.map(({ t, s }) => {
              const da = fermo(s) ? giorniDa(t.blockedSince) : null;
              const motivo = t.blockedReason?.trim() || 'motivo non scritto';
              const sub = s === 'FERMO_CLIENTE'
                ? <><span className="is-warn">Aspettiamo il cliente</span>: {motivo}{da != null ? ` · ${daQuanto(da)}` : ''}</>
                : fermo(s)
                  ? <>{STATI[s].breve}: {motivo}{da != null ? ` · ${daQuanto(da)}` : ''}</>
                  : [STATI[s].breve, personaBreve(t.assignee), t.dueDate && s !== 'DONE' ? `entro il ${giornoBreve(t.dueDate)}` : null].filter(Boolean).join(' · ');
              return (
                <MRow
                  key={t.id}
                  icon={ICONA[s]}
                  // Il rosso solo dove tocca a noi: il cliente che non risponde
                  // si scrive in arancio nella riga sotto, non è un nostro allarme.
                  tone={s === 'FERMO_NOI' || s === 'FERMO' ? 'alert' : undefined}
                  title={s === 'DONE' ? <span className="pjm-barrato">{t.title}</span> : t.title}
                  sub={sub}
                  subWrap={fermo(s)}
                  onClick={() => setMenu(t)}
                />
              );
            })}
            {lavori.length > PRIMI && (
              <MMore label={tutti ? 'Mostra meno' : `Vedi tutti i ${lavori.length}`} onClick={() => setTutti((v) => !v)} />
            )}
            {campo}
            <RigaAzione icon="file" label="Carica dal preventivo" onClick={() => setImporta(true)} />
          </>
        )}
      </MGroup>

      {/* Le stesse voci della tendina del computer (MenuStato), dal basso. */}
      <MobileSheet open={!!menu} onClose={() => setMenu(null)} title={menu?.title}>
        {menu && statoMenu && (
          <div className="pjm-sheet">
            <div className="pjm-meta">
              Adesso: {STATI[statoMenu].breve.toLowerCase()}
              {fermo(statoMenu) && menu.blockedReason ? ` (${menu.blockedReason.trim()})` : ''}
              {menu.quoteRef ? ` · ${menu.quoteRef}` : ''}
            </div>
            <div role="menu" aria-label="Cambia lo stato del lavoro">
              {VOCI_STATO.filter((v) => v.s !== statoMenu).map((v) => (
                <button key={v.s} type="button" role="menuitem" className="m-sheet-row m-act-row" onClick={() => { const t = menu; setMenu(null); cambiaStato(t, v.s); }}>
                  <Icon name={v.icon} size={16} className="m-act-ico" />
                  <span className="m-sheet-row-lbl">{v.label}</span>
                </button>
              ))}
              <button type="button" role="menuitem" className="m-sheet-row m-act-row" onClick={() => { const t = menu; setMenu(null); apriTask(t.id); }}>
                <Icon name="eye" size={16} className="m-act-ico" />
                <span className="m-sheet-row-lbl">Apri il task del lavoro</span>
              </button>
              <div className="m-act-danger">
                <button
                  type="button"
                  role="menuitem"
                  className="m-sheet-row m-act-row"
                  onClick={() => {
                    const t = menu;
                    setMenu(null);
                    aggiorna.mutate({ id: t.id, body: { inScope: false } }, {
                      onSuccess: () => toast.ok('Tolto dai lavori previsti', `"${t.title}" resta tra i task del progetto.`),
                    });
                  }}
                >
                  <Icon name="x" size={16} className="m-act-ico" />
                  <span className="m-sheet-row-lbl">Togli dai lavori previsti</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </MobileSheet>

      {importa && <SuTutto><CaricaDalPreventivo projectId={projectId} onClose={() => setImporta(false)} /></SuTutto>}
      {resoconto && <SuTutto><ResocontoAlCliente projectId={projectId} onClose={() => setResoconto(false)} /></SuTutto>}
      {inFermo && (
        <SuTutto>
          <FermoDialog
            lavoro={inFermo.lavoro}
            chi={inFermo.chi}
            onClose={() => setInFermo(null)}
            onConferma={(motivo) => aggiorna.mutate(
              { id: inFermo.lavoro.id, body: { status: 'BLOCKED', blockedBy: inFermo.chi, blockedReason: motivo } },
              { onSuccess: () => setInFermo(null) },
            )}
            inCorso={aggiorna.isPending}
          />
        </SuTutto>
      )}
    </>
  );
}

/** Quanti lavori sono fatti e quanti fermi: la riga sotto la risposta della scheda. */
export function riassuntoLavori(tasks: LavoroTask[]) {
  const lavori = tasks.filter((t) => t.inScope).map((t) => statoDi(t));
  return {
    totale: lavori.length,
    fatti: lavori.filter((s) => s === 'DONE').length,
    attesaCliente: lavori.filter((s) => s === 'FERMO_CLIENTE').length,
    fermiNoi: lavori.filter((s) => s === 'FERMO_NOI' || s === 'FERMO').length,
  };
}
